import './MyAccountContainer.css';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CenterFrameLayout } from 'Layouts';
import { trackEvent, segmentTrackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { getCardPaymentMethodId, trackPaymentStarted } from 'Utils/PaymentMethods';
import { fetchProfileInfo } from 'State/User/UserActions';
import { clearConfirmation } from 'State/Layouts/LayoutActions';
import { payment, fetchPaymentMethods } from 'State/PaymentMethods/PaymentMethodActions';
import { fetchPayments } from 'State/Payments/PaymentActions';
import { getPlans, selectRestartPlan } from 'State/Plans/PlansActions';
import { fetchSubscriptions, fetchSubscriptionStatus } from 'State/Subscriptions/SubscriptionsActions';
import { reactivateSubscription } from 'Api/AccountApi';
import { ConfirmationMsg } from 'Components/ConfirmationMsg';
import { ManageProfile } from 'Features/MyAccount/ManageProfile';
import { Membership } from 'Features/MyAccount/Membership';
import { ManageBilling } from 'Features/MyAccount/ManageBilling';
import { RedfastZone } from 'Components/RedfastZone';
import { ManagePromotion } from 'Features/MyAccount/ManagePromotion';
import { RestartPlanModal } from 'Features/MyAccount/RestartPlanModal';
import { ResumePlanModal } from 'Features/MyAccount/ResumePlanModal';
import { UpdatePaymentModal } from 'Features/MyAccount/UpdatePaymentModal';
import { RestartConfirmationModal } from 'Features/MyAccount/RestartPlanChurned/RestartConfirmationModal';
import { PAYMENT_METHODS } from 'Features/PaymentMethodSelector/PaymentMethodSelector';
import { SectionComponent } from 'Features/MyAccount/SectionComponent';
import { usePromoCode } from 'Hooks/usePromoCode';
import { usePauseSubscription } from 'Hooks/usePauseSubscription';
import { updateCardPayment } from 'Api/EvergentApi';
import { getNumberOfDaysForPause } from 'Utils/Date';
import { PromoBannerContainer } from 'Features/MyAccount/PromoBanner/PromoBannerContainer';
import BANNER_CONFIG from 'Config/BannerCatalog.json';
import { getActivePlanSimplifiedName, getPlanFromList } from 'Utils/Plans';
import { trackPage } from 'Utils/Analytics';
import { useLocation, useNavigate } from 'react-router-dom';
import { isEmptyObj } from 'Utils/Utils';
import { usePlans } from 'Hooks/usePlans';
import { setPurchaseMode } from 'State/PurchaseMode/PurchaseModeActions';
import { MANAGE_PLAN_PURCHASE_MODE } from 'Utils/Constants';

const trackEditAccount = (itemTitle) => {
  segmentTrackEvent(TRACKING_ENUMS.EVENT_UPDATE_ACCOUNT_STARTED);
  trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_SETTING_SELECTED, {
    page_item_title: itemTitle,
  });
};

export function MyAccountContainer() {
  const dispatch = useDispatch();
  const { applyCoupon } = usePromoCode();
  const [isLoading, setLoading] = useState(true);
  const [showReinstateModal, toggleReinstateModal] = useState(false);
  const [showRestartChurnedModal, toggleRestartChurnedModal] = useState(false);
  const [showResumePlanModal, toggleResumePlanModal] = useState(false);
  const [showRestartChurnedModalSuccess, toggleRestartChurnedModalSuccess] = useState(false);
  const [showApplyCouponPaymentModal, toggleApplyCouponPaymentModal] = useState(false);

  const paymentMethods = useSelector((s) => s.paymentMethods);
  const { couponCode = '' } = useSelector((s) => s.promoCode);
  const user = useSelector((s) => s.user);
  const subscription = useSelector((s) => s.subscription);
  const routes = useSelector((s) => s.appConfig.routes);
  const { isConfirmSuccess, confirmationMsg } = useSelector((s) => s.pageLayout);
  const { hasPayments, nextBillInfo, orders } = useSelector((s) => s.payments);
  const { rootDomain, enablePauseSubscription, entitlementId, showAccountPromoCode } = useSelector(
    (s) => s.appConfig.defaultConfig
  );
  const { allPlans } = usePlans();
  const navigate = useNavigate();
  const { state } = useLocation();
  const showPlanSelectModal = state?.showPlanSelectModal;

  const nextBillingDateTime = nextBillInfo && nextBillInfo.nextBillingDateTime;
  const {
    activeSubscriptionId,
    activeSubscriptionTier,
    isChurnedRestartAvailable,
    restartChurnedUserDetails,
    isPendingCancel,
    isExistingPlanUser,
    isIAPUser,
    accountPromotionSegment,
    nextSubscription,
    isSuccess,
    hasSubscription,
    isChangePlanAvailable,
  } = subscription;
  const { restartPlan, paymentMethodRequired } = restartChurnedUserDetails;
  const { isAuthenticated } = user;

  const promoBanner = BANNER_CONFIG[accountPromotionSegment.segmentType] || false;

  let activePlanSimplifiedName = getActivePlanSimplifiedName(allPlans, activeSubscriptionId);
  if (!activePlanSimplifiedName && nextSubscription?.serviceID) {
    const nextPlanName = getActivePlanSimplifiedName(allPlans, nextSubscription.serviceID);
    if (nextPlanName) activePlanSimplifiedName = `Upcoming Plan: ${nextPlanName}`;
  }
  let activePlan = getPlanFromList(activeSubscriptionId, allPlans);
  activePlan = isEmptyObj(activePlan) ? null : activePlan;

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchProfileInfo());
    }
    return function unMountMyAccount() {
      dispatch(clearConfirmation());
    };
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchSubscriptions());
      dispatch(fetchSubscriptionStatus());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchPayments(entitlementId));
      dispatch(fetchPaymentMethods());
    }
    setLoading(false);
  }, [dispatch, entitlementId, isAuthenticated]);

  // set manage purchaseMode for existing active subscription users.
  useEffect(() => {
    if (isSuccess && hasSubscription && isChangePlanAvailable) {
      dispatch(setPurchaseMode(MANAGE_PLAN_PURCHASE_MODE));
    }
  }, [dispatch, isSuccess, hasSubscription, isChangePlanAvailable]);

  useEffect(() => {
    if (accountPromotionSegment.segmentType === 'Military Segment Type') {
      trackPage('Account Upgrade Option Offer', window.location.pathname, 'account_upgrade_option_offer');
    }
  }, [accountPromotionSegment.segmentType]);

  const [pauseState, pauseActions] = usePauseSubscription(
    enablePauseSubscription && isExistingPlanUser && isAuthenticated && activeSubscriptionTier === 100
  );

  const isPauseEnabled = useMemo(() => {
    return pauseState.noOfDaysLeft >= getNumberOfDaysForPause(nextBillingDateTime, 1) && pauseState.noOfTimesLeft > 0;
  }, [pauseState, nextBillingDateTime]);

  const handleRestartChurnedPlan = async () => {
    const planDetails = {
      page_plan_id: restartPlan.planId,
      page_plan_name: restartPlan.displayName,
      page_plan_price: restartPlan.retailPrice,
    };

    const paymentMethodType = paymentMethodRequired ? 'card' : 'saved';
    const paymentmethodInfo = getCardPaymentMethodId(paymentMethods.cardPayment);
    trackPaymentStarted({
      page_start_source: 'my account',
      page_plan_flow_type: 'reinstate plan',
      page_payment_method: PAYMENT_METHODS.CARD,
    });
    return dispatch(
      payment(
        paymentMethodType,
        { isSkipDefaultPromo: true, paymentmethodInfo },
        PAYMENT_METHODS.CARD,
        'my account',
        'reinstate plan'
      )
    )
      .then(() => {
        trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESTART_PLAN_COMPLETED, planDetails);
        toggleRestartChurnedModalSuccess(true);
        toggleRestartChurnedModal(true);
      })
      .catch((error) => {
        return onSubmitReactFormError(error.message);
      });
  };

  const handleRestartPlan = async (showConfirmationModal = true) => {
    // churned user - add new order
    if (isChurnedRestartAvailable) {
      await dispatch(selectRestartPlan(restartPlan));
      const churnedPlanDetails = {
        page_plan_id: restartPlan.planId,
        page_plan_name: restartPlan.displayName,
        page_plan_price: restartPlan.retailPrice,
      };
      trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESTART_PLAN_STARTED, churnedPlanDetails);
      if (paymentMethodRequired) {
        return toggleRestartChurnedModal(true);
      }
      return handleRestartChurnedPlan();
    }

    // pending cancel user - reactivate an existing plan
    const planDetails = {
      page_plan_id: activeSubscriptionId || '',
      page_plan_name: (nextBillInfo && nextBillInfo.planName) || '',
      page_plan_price: (nextBillInfo && nextBillInfo.planPrice) || '',
    };
    trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESTART_PLAN_STARTED, planDetails);
    const { isSuccess } = await reactivateSubscription(activeSubscriptionId);
    if (isSuccess) {
      await dispatch(fetchSubscriptions());
      await dispatch(fetchSubscriptionStatus());
      await dispatch(fetchPayments(entitlementId));
      trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESTART_PLAN_COMPLETED, planDetails);
    }
    return toggleReinstateModal(showConfirmationModal && isSuccess);
  };

  const handleResumeSubscription = async () => {
    const result = await pauseActions.resume(activeSubscriptionId);
    const pauseTrackingResumeTracking = {
      page_start_source: 'my account',
      page_plan_name: (nextBillInfo && nextBillInfo.planName) || '',
      page_plan_price: (nextBillInfo && nextBillInfo.planPrice) || '',
      page_plan_id: activeSubscriptionId,
    };

    if (result.isSuccess) {
      dispatch(fetchSubscriptions());
      dispatch(fetchSubscriptionStatus());
      trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESUME_COMPLETED, pauseTrackingResumeTracking);
    } else {
      const errorObj = {
        page_error_description: 'Pause Resume Subscription Error',
        page_error_type: result,
      };
      const pauseResumeErrorTracking = { ...pauseTrackingResumeTracking, ...errorObj };
      trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESUME_ERROR, pauseResumeErrorTracking);
    }
    toggleResumePlanModal(false);
  };

  const handleCancelPauseSubscription = async () => {
    const { isSuccess } = await pauseActions.cancelPause(activeSubscriptionId);

    if (isSuccess) {
      dispatch(fetchSubscriptions());
      dispatch(fetchSubscriptionStatus());
      dispatch(fetchPayments(entitlementId));
    }
  };

  const handleApplyPromoCode = async ({ promoCode }) => {
    let isPaymentTypeError = false;
    try {
      const { isPaymentMethodError, isSuccess, errorMessage } = await applyCoupon(promoCode);
      isPaymentTypeError = isPaymentMethodError;
      if (!isSuccess) {
        throw new Error(errorMessage);
      }
      await dispatch(fetchPayments(entitlementId));
      return true;
    } catch (error) {
      if (isPaymentTypeError) {
        toggleApplyCouponPaymentModal(true);
      }
      return onSubmitReactFormError(error.message);
    }
  };

  const handleUpdatePaymentApplyCoupon = async (code) => {
    try {
      const { isSuccess, errorMessage } = await updateCardPayment();
      if (!isSuccess) {
        throw new Error(errorMessage);
      }
      toggleApplyCouponPaymentModal(false);
      await dispatch(fetchPaymentMethods());
      await handleApplyPromoCode({ promoCode: code });
      return true;
    } catch (error) {
      return onSubmitReactFormError(error.message);
    }
  };

  return (
    <CenterFrameLayout title="My Account" showLoader={isLoading}>
      <div className="settingsSection">
        {isConfirmSuccess && <ConfirmationMsg text={confirmationMsg} />}
        <ManageProfile user={user} subTitle="Manage Account" routes={routes} trackEditAccount={trackEditAccount} />
        {promoBanner && (
          <SectionComponent
            subTitle="Promotion"
            sectionRight={<PromoBannerContainer promoBanner={promoBanner} useDetailsBanner={false} />}
          />
        )}
        {!promoBanner && <RedfastZone name="MyAccountTop" />}
        <ManageBilling
          title="Manage Billing"
          user={user}
          subscription={subscription}
          paymentMethods={paymentMethods}
          nextBillInfo={nextBillInfo}
          hasPayments={hasPayments}
          routes={routes}
          trackEditAccount={trackEditAccount}
          orders={orders}
        />
        <Membership
          title="Manage Subscription"
          user={user}
          subscription={subscription}
          paymentMethods={paymentMethods}
          nextBillInfo={nextBillInfo}
          routes={routes}
          trackEditAccount={trackEditAccount}
          onRestartPlan={handleRestartPlan}
          toggleResumePlanModal={toggleResumePlanModal}
          handleCancelPauseSubscription={handleCancelPauseSubscription}
          isPauseEnabled={isPauseEnabled}
          activePlanSimplifiedName={activePlanSimplifiedName}
          activePlan={activePlan}
          allPlans={allPlans}
          openPlanSelectModal={showPlanSelectModal}
          navigate={navigate}
          state={state}
        />
        {promoBanner && promoBanner.promotionDetailsBanner && (
          <SectionComponent
            subTitle="Promotion Details"
            sectionRight={<PromoBannerContainer promoBanner={promoBanner} useDetailsBanner={true} />}
          />
        )}
        {!promoBanner && <RedfastZone name="MyAccount2nd" />}
        {showAccountPromoCode &&
          !subscription.isNewUser &&
          !isPendingCancel &&
          !paymentMethods.hasGiftingPayment &&
          !isIAPUser && <ManagePromotion subTitle="Promotion Code" onSubmitPromoCode={handleApplyPromoCode} />}
      </div>
      {/* for reinstating pendingCancel - not for restarting churned */}
      {showReinstateModal && (
        <RestartPlanModal
          isShowing={showReinstateModal}
          onClose={() => toggleReinstateModal(false)}
          planName={activePlanSimplifiedName}
        />
      )}
      {showRestartChurnedModal && (
        <RestartConfirmationModal
          onClose={() => toggleRestartChurnedModal(false)}
          onSuccessClick={() => window.location.replace(rootDomain)}
          paymentMethodRequired={paymentMethodRequired}
          onPaymentSubmit={handleRestartChurnedPlan}
          showSuccessModal={showRestartChurnedModalSuccess}
        />
      )}
      {showResumePlanModal && (
        <ResumePlanModal
          isShowing={showResumePlanModal}
          onClose={() => toggleResumePlanModal(false)}
          handleResumeSubscription={handleResumeSubscription}
        />
      )}
      {showApplyCouponPaymentModal && (
        <UpdatePaymentModal
          isShowing={showApplyCouponPaymentModal}
          onPaymentSubmit={() => handleUpdatePaymentApplyCoupon(couponCode)}
          onClose={() => toggleApplyCouponPaymentModal(false)}
        />
      )}
    </CenterFrameLayout>
  );
}
